import { useMemo, useState, type FormEvent } from "react";
import { Modal } from "../components/Modal";
import { StatusBadge } from "../components/StatusBadge";
import { buildWorkItemTree, type WorkItemNode } from "../lib/tree";
import { orderedStatuses, statusDefinitions } from "../lib/status";
import type {
  CreateWorkItemInput,
  ItemType,
  ProjectRole,
  WorkItem,
  WorkStatus,
} from "../types/domain";

interface RoadmapViewProps {
  items: WorkItem[];
  role: ProjectRole | null;
  onStatusChange(itemId: string, status: WorkStatus, reason: string | null): Promise<void>;
  onCreate(input: CreateWorkItemInput): Promise<void>;
  projectId: string;
}

function RoadmapNode({
  node,
  canEdit,
  onStatusChange,
}: {
  node: WorkItemNode;
  canEdit: boolean;
  onStatusChange(itemId: string, status: WorkStatus): Promise<void>;
}) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className={`roadmap-node roadmap-node--${node.itemType}`}>
      <div className="roadmap-node__line">
        <button
          className="roadmap-node__toggle"
          type="button"
          onClick={() => setExpanded(!expanded)}
          disabled={node.children.length === 0}
          aria-label={expanded ? "Contraer" : "Expandir"}
        >
          {node.children.length === 0 ? "·" : expanded ? "−" : "+"}
        </button>
        <div className="roadmap-node__content">
          <div className="roadmap-node__title">
            <span className="item-type-label">{node.itemType}</span>
            <strong>{node.title}</strong>
            {node.isCurrentFocus && <span className="focus-chip">Foco</span>}
          </div>
          {node.description && <p>{node.description}</p>}
        </div>
        <div className="roadmap-node__meta">
          <span className="weight-chip">Peso {node.weight}</span>
          {canEdit ? (
            <select
              value={node.status}
              onChange={(event) => void onStatusChange(node.id, event.target.value as WorkStatus)}
              aria-label={`Estado de ${node.title}`}
            >
              {orderedStatuses.map((status) => (
                <option key={status} value={status}>
                  {statusDefinitions[status].label}
                </option>
              ))}
            </select>
          ) : (
            <StatusBadge status={node.status} compact />
          )}
        </div>
      </div>
      {expanded && node.children.length > 0 && (
        <div className="roadmap-node__children">
          {node.children.map((child) => (
            <RoadmapNode
              key={child.id}
              node={child}
              canEdit={canEdit}
              onStatusChange={onStatusChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function RoadmapView({
  items,
  role,
  onStatusChange,
  onCreate,
  projectId,
}: RoadmapViewProps) {
  const tree = useMemo(() => buildWorkItemTree(items), [items]);
  const canEdit = role === "owner" || role === "editor";
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<CreateWorkItemInput>({
    projectId,
    parentId: null,
    itemType: "phase",
    title: "",
    description: "",
    status: "planned",
    weight: 1,
    sortOrder: items.length * 10 + 10,
    isCurrentFocus: false,
    targetDate: null,
  });

  const changeStatus = async (itemId: string, status: WorkStatus) => {
    const reason = window.prompt("Motivo de la transición (opcional):")?.trim() || null;
    await onStatusChange(itemId, status, reason);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await onCreate(form);
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="view-stack">
      <section className="section-toolbar">
        <div>
          <span className="eyebrow">Jerarquía única</span>
          <h2>Roadmap</h2>
          <p>Fases, bloques, tareas y subtareas comparten un contrato y profundidad controlada.</p>
        </div>
        {canEdit && (
          <button className="button button--primary" type="button" onClick={() => setOpen(true)}>
            Añadir elemento
          </button>
        )}
      </section>
      <section className="panel roadmap-panel">
        {tree.map((node) => (
          <RoadmapNode key={node.id} node={node} canEdit={canEdit} onStatusChange={changeStatus} />
        ))}
      </section>
      <Modal open={open} title="Añadir elemento al roadmap" onClose={() => setOpen(false)}>
        <form className="form-stack" onSubmit={(event) => void submit(event)}>
          <label>
            Tipo
            <select
              value={form.itemType}
              onChange={(event) => {
                const itemType = event.target.value as ItemType;
                setForm({
                  ...form,
                  itemType,
                  parentId: itemType === "phase" ? null : form.parentId,
                });
              }}
            >
              <option value="phase">Fase</option>
              <option value="block">Bloque</option>
              <option value="task">Tarea</option>
              <option value="subtask">Subtarea</option>
            </select>
          </label>
          {form.itemType !== "phase" && (
            <label>
              Padre
              <select
                required
                value={form.parentId ?? ""}
                onChange={(event) => setForm({ ...form, parentId: event.target.value })}
              >
                <option value="">Seleccionar…</option>
                {items
                  .filter((item) => item.depth < 3)
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {"—".repeat(item.depth)} {item.title}
                    </option>
                  ))}
              </select>
            </label>
          )}
          <label>
            Título
            <input
              required
              value={form.title}
              onChange={(event) => setForm({ ...form, title: event.target.value })}
            />
          </label>
          <label>
            Descripción
            <textarea
              rows={3}
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </label>
          <div className="form-grid">
            <label>
              Estado
              <select
                value={form.status}
                onChange={(event) => setForm({ ...form, status: event.target.value as WorkStatus })}
              >
                {orderedStatuses.map((status) => (
                  <option key={status} value={status}>
                    {statusDefinitions[status].label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Peso
              <input
                min={0.1}
                max={1000}
                step={0.1}
                type="number"
                value={form.weight}
                onChange={(event) => setForm({ ...form, weight: Number(event.target.value) })}
              />
            </label>
          </div>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={form.isCurrentFocus}
              onChange={(event) => setForm({ ...form, isCurrentFocus: event.target.checked })}
            />{" "}
            Marcar como foco actual
          </label>
          <div className="form-actions">
            <button
              className="button button--secondary"
              type="button"
              onClick={() => setOpen(false)}
            >
              Cancelar
            </button>
            <button className="button button--primary" disabled={submitting} type="submit">
              {submitting ? "Guardando…" : "Crear"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
